import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT } from "../data/site";
import { useI18n } from "../i18n/LanguageContext";

const WaIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.2-1.7-.8-2-.9-.3-.1-.5-.2-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-1.7-.8-2.8-1.5-3.9-3.4-.3-.5.3-.5.8-1.5.1-.2 0-.4 0-.5s-.7-1.7-1-2.3c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.3 5.2 4.6 1.9.8 2.6.9 3.6.8.6-.1 1.7-.7 2-1.4.2-.7.2-1.2.1-1.4 0-.1-.2-.2-.5-.4zM12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2z" />
  </svg>
);

const LangSwitch = ({ dark = false }) => {
  const { lang, setLang } = useI18n();
  const base = "px-2.5 py-1 rounded-full text-xs font-semibold transition-colors duration-300";
  return (
    <div data-testid="lang-switch" className={`flex items-center rounded-full p-0.5 ${dark ? "bg-white/10" : "bg-navy/8"}`}>
      {["en", "nl"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          aria-pressed={lang === l}
          className={`${base} ${lang === l ? "bg-gold text-white" : dark ? "text-white/70" : "text-navy/60"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useI18n();

  const LINKS = [
    { key: "projects", label: t.nav.projects, to: "/projects" },
    { key: "distress-deals", label: t.nav.distress, to: "/distress" },
    { key: "about", label: t.nav.about, to: "/about" },
    { key: "blog", label: t.nav.blog, to: "/blog" },
    { key: "contact", label: t.nav.contact, to: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-[padding,background,box-shadow] duration-500 ${
          scrolled ? "bg-cream/90 py-3 shadow-soft backdrop-blur-md" : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="container-x flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link"><Logo /></Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.key}`}
                className={({ isActive }) =>
                  `text-base font-medium transition-colors duration-300 hover:text-gold ${isActive ? "text-gold" : "text-navy/75"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block"><LangSwitch /></div>
            <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid="nav-whatsapp-btn" className="btn-wa hidden sm:inline-flex">
              <WaIcon /> {t.nav.whatsapp}
            </a>
            <button onClick={() => setOpen(true)} data-testid="nav-menu-open" className="rounded-full p-1.5 text-navy lg:hidden" aria-label="Open menu">
              <Menu size={26} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-navy lg:hidden" data-testid="mobile-menu">
            <div className="container-x flex items-center justify-between py-5">
              <Logo variant="light" />
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" aria-label="Close menu"><X size={28} className="text-white" /></button>
            </div>
            <nav className="container-x flex flex-col pt-6">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.08 + i * 0.06 }}>
                  <Link to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${l.key}`} className="block border-b border-white/10 py-5 font-display text-3xl font-medium text-white">
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <div className="mt-6"><LangSwitch dark /></div>
              <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-btn" className="btn-wa mt-6">
                <WaIcon size={18} /> {t.nav.chat}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
